import config from 'config';

export type Bucket = { remaining: number; limit: number; reset: number };

export const NO_LIMITS_BUCKET: Bucket = {
  reset: 0,
  limit: Number.MAX_SAFE_INTEGER,
  remaining: Number.MAX_SAFE_INTEGER,
};

export class RateLimiter {
  #buckets = new Map<string, Bucket>();
  #pendingRequests = new Map<string, Set<string>>();
  #queues = new Map<string, Map<string, PromiseWithResolvers<void>>>();
  #timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  #threshold = config.rateLimiterThrottleThreshold ?? 0.3;
  #maxParallelRequests = 8;

  #bottleneck: Bucket = { ...NO_LIMITS_BUCKET };

  constructor(defaultLimit: string) {
    this.initBottleneckBucket(defaultLimit);
  }

  get buckets(): Record<string, Bucket> {
    return Object.fromEntries(
      Array.from(this.#buckets.entries()).map(([key, bucket]) => [
        key,
        { ...bucket, remaining: bucket.remaining - this.getPendingCount(key) },
      ]),
    );
  }

  get bottleneckSerialized(): string {
    return `${this.#bottleneck.remaining};${this.#bottleneck.reset};${this.#bottleneck.limit}`;
  }

  getBucketNameForPath(path: string): string {
    return this.#buckets.has(path) ? path : 'global';
  }

  updateBuckets(payload: Record<string, Bucket>): void {
    const keys = new Set(Object.keys(payload));

    for (const key of this.#buckets.keys()) {
      keys.add(key);
    }

    for (const key of keys) {
      let bucket: Bucket | undefined;

      const bucketA = payload[key];
      const bucketB = this.#buckets.get(key);

      if (this.isBucket(bucketA)) {
        if (this.isBucket(bucketB)) {
          bucket = bucketB.remaining < bucketA.remaining ? bucketB : bucketA;
        } else {
          bucket = bucketA;
        }
      } else {
        if (this.isBucket(bucketB)) {
          bucket = bucketB;
        }
      }

      if (bucket) {
        this.updateBucket(key, bucket);
      }
    }
  }

  updateBucket(path: string, rateLimit: Bucket): void {
    if (rateLimit.remaining < this.#bottleneck.remaining) {
      this.#bottleneck = {
        limit: rateLimit.limit,
        reset: rateLimit.reset,
        remaining: rateLimit.remaining,
      };
    }

    const bucket = this.#buckets.get(path) ?? rateLimit;

    bucket.limit = rateLimit.limit;
    bucket.reset = rateLimit.reset;
    bucket.remaining = rateLimit.remaining;

    this.#buckets.set(path, bucket);
  }

  getBucket(path: string): Bucket {
    const bucket = this.#buckets.get(path);

    if (bucket && bucket.reset > Date.now()) {
      return bucket;
    }

    if (this.#bottleneck.reset < Date.now()) {
      this.#bottleneck = { ...NO_LIMITS_BUCKET };
    }

    return this.#bottleneck;
  }

  private getRemaining(path: string): number {
    return this.getBucket(path).remaining - this.getPendingCount(path);
  }

  private getLimit(path: string): number {
    return this.getBucket(path).limit;
  }

  getPendingCount(path: string): number {
    return this.#pendingRequests.get(path)?.size ?? 0;
  }

  resolveRequest(path: string, id: string): void {
    this.getPendingRequests(path).delete(id);
  }

  processRequest(path: string, id: string): void {
    this.getPendingRequests(path).add(id);
  }

  restart(timeout: number): void {
    for (const timeoutId of this.#timeouts.values()) {
      clearTimeout(timeoutId);
    }

    for (const path of this.#queues.keys()) {
      const jitter = timeout * Math.random() * 0.25;

      this.scheduleLockRelease(path, timeout + jitter);
    }
  }

  getQueue(path: string): Map<string, PromiseWithResolvers<void>> {
    if (!this.#queues.has(path)) {
      this.#queues.set(path, new Map<string, PromiseWithResolvers<void>>());
    }

    return this.#queues.get(path) as Map<string, PromiseWithResolvers<void>>;
  }

  getPendingRequests(path: string): Set<string> {
    if (!this.#pendingRequests.has(path)) {
      this.#pendingRequests.set(path, new Set<string>());
    }

    return this.#pendingRequests.get(path) as Set<string>;
  }

  hasQueuedRequests(): boolean {
    for (const queue of this.#queues.values()) {
      if (queue.size > 0) {
        return true;
      }
    }

    return false;
  }

  canProcessAtNormalPace(path: string): boolean {
    return !this.isHittingRateLimit(path) && this.getQueue(path).size === 0;
  }

  isHittingRateLimit(path: string): boolean {
    return this.getRemaining(path) < this.getLimit(path) * this.#threshold;
  }

  async queueRequest(path: string, id: string, isOnHold?: boolean): Promise<void> {
    const lock = Promise.withResolvers<void>();

    this.getQueue(path).set(id, lock);

    if (!isOnHold) {
      this.scheduleLockRelease(path);
    }

    await lock.promise;
  }

  deleteBucket(path: string): void {
    this.#buckets.delete(path);
  }

  scheduleLockRelease(path: string, timeout = 1000, force = false): void {
    const timeoutId = this.#timeouts.get(path);

    if (force && timeoutId) {
      clearTimeout(timeoutId);
      this.#timeouts.delete(path);
    }

    if (this.#timeouts.get(path) || this.getQueue(path).size === 0) {
      if (this.getQueue(path).size === 0) {
        this.#timeouts.delete(path);
      }

      return;
    }

    this.#timeouts.set(
      path,
      setTimeout(() => {
        this.#timeouts.delete(path);
        this.processQueue(path);
      }, timeout),
    );
  }

  processQueue(path: string): void {
    const queue = this.getQueue(path);
    const limit = this.getLimit(path);
    const remaining = this.getRemaining(path);

    if (this.getPendingCount(path) > this.#maxParallelRequests) {
      this.scheduleLockRelease(path);

      return;
    }

    // Release 10% of the remaining requests if the remaining requests are greater than 50% of the limit
    if (remaining >= limit * 0.5) {
      this.releaseLock(path, queue, Math.floor(remaining * 0.1));
      this.scheduleLockRelease(path);

      return;
    }

    // Release 5% of the remaining requests if the remaining requests are greater than 20% of the limit
    if (remaining >= limit * 0.2) {
      this.releaseLock(path, queue, Math.floor(remaining * 0.05));
      this.scheduleLockRelease(path);

      return;
    }

    // Release 1 request if the remaining requests are greater than 0
    if (remaining > 0) {
      this.releaseLock(path, queue, 1);
      this.scheduleLockRelease(path);
    } else {
      const resetsAt = this.getBucket(path).reset;
      this.scheduleLockRelease(path, resetsAt ? resetsAt - Date.now() : 30_000, true);
    }
  }

  private isBucket(bucket: Record<string, unknown> | undefined): bucket is Bucket {
    if (!bucket) {
      return false;
    }

    return (
      'remaining' in bucket &&
      'reset' in bucket &&
      'limit' in bucket &&
      Array.isArray(bucket.limit) &&
      bucket.limit.length > 0
    );
  }

  private initBottleneckBucket(defaultLimit: string): void {
    const [remaining, reset, limit] = defaultLimit.split(';').map(Number);

    if (Number.isInteger(remaining) && Number.isInteger(reset) && Number.isInteger(limit)) {
      this.#bottleneck = { reset, limit, remaining };
    }
  }

  private releaseLock(
    path: string,
    queue: Map<string, PromiseWithResolvers<void>>,
    size: number,
  ): void {
    const entries = Array.from(queue.entries());
    const pendingRequests = this.getPendingRequests(path);

    for (let i = 0; i < Math.min(this.#maxParallelRequests, entries.length, size); i++) {
      const [lockId, lockResolver] = entries[i];

      pendingRequests.add(lockId);

      lockResolver.resolve();
      queue.delete(lockId);
    }
  }
}
